import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useEffect, useMemo } from "react";
import type { BulkRescheduleRehearsalSessionsDTO } from "../../domain/contracts/rehearsal-sessions";
import { formatDateLocal } from "@/shared/lib/date-time-performance";
import { RehearsalStudioBookingFields } from "./RehearsalStudioBookingFields";
import {
  buildRescheduleDefaultValues,
  buildStudioBookingPayloadFromSlice,
  rescheduleDayFormSchema,
  type RescheduleDayFormValues,
  toRescheduleConflictValues,
} from "./rehearsal-session-form-types";
import {
  RehearsalStudioConflictProvider,
  useRehearsalStudioConflict,
  useRehearsalStudioConflictMonitor,
} from "./rehearsal-studio-conflict";

type StudioSessionInfo = {
  roomBookingId?: string | null;
  roomId?: string | null;
  roomName?: string | null;
  bookingStartTime?: string | null;
  bookingEndTime?: string | null;
};

type RehearsalDayRescheduleDialogProps = {
  open: boolean;
  fromScheduledDate: string;
  performerCount: number;
  studioSession?: StudioSessionInfo | null;
  canBookStudio?: boolean;
  submitting?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: BulkRescheduleRehearsalSessionsDTO) => Promise<void>;
};

function RescheduleDialogFooter({
  submitting,
  unchanged,
  onCancel,
}: {
  submitting?: boolean;
  unchanged: boolean;
  onCancel: () => void;
}) {
  const { submitBlocked, isChecking } = useRehearsalStudioConflict();

  return (
    <DialogFooter className="gap-2">
      <Button
        type="button"
        variant="outline"
        disabled={submitting}
        onClick={onCancel}
      >
        ຍົກເລີກ
      </Button>
      <Button
        type="submit"
        disabled={submitting || unchanged || submitBlocked}
      >
        {isChecking ? "ກຳລັງກວດສອບ..." : "ບັນທຶກ"}
      </Button>
    </DialogFooter>
  );
}

export function RehearsalDayRescheduleDialog({
  open,
  fromScheduledDate,
  performerCount,
  studioSession,
  canBookStudio = false,
  submitting,
  onOpenChange,
  onSubmit,
}: RehearsalDayRescheduleDialogProps) {
  const hasStudioBooking = Boolean(studioSession?.roomBookingId);

  const defaultValues = useMemo(
    () =>
      buildRescheduleDefaultValues({
        fromScheduledDate,
        studioSession,
      }),
    [fromScheduledDate, studioSession],
  );

  const methods = RHF.useForm<RescheduleDayFormValues>({
    resolver: zodResolver(rescheduleDayFormSchema),
    defaultValues,
  });

  useEffect(() => {
    if (!open) return;
    methods.reset(defaultValues);
  }, [open, defaultValues, methods]);

  const scheduledDate = methods.watch("scheduledDate");
  const bookStudio = methods.watch("bookStudio");
  const studioRoomId = methods.watch("studioRoomId");
  const studioStartTime = methods.watch("studioStartTime");
  const studioSlotMinutes = methods.watch("studioSlotMinutes");

  const unchanged =
    scheduledDate === fromScheduledDate &&
    (!hasStudioBooking ||
      (studioRoomId === defaultValues.studioRoomId &&
        studioStartTime === defaultValues.studioStartTime &&
        studioSlotMinutes === defaultValues.studioSlotMinutes));

  const studioConflictMonitor = useRehearsalStudioConflictMonitor(
    canBookStudio,
    {
      bookStudio: hasStudioBooking ? bookStudio : false,
      studioRoomId,
      scheduledDates: scheduledDate ? [scheduledDate] : [],
      studioStartTime,
      studioSlotMinutes,
      excludeRoomBookingId: studioSession?.roomBookingId ?? undefined,
    },
    open && hasStudioBooking,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>ແກ້ໄຂວັນຊ້ອມທັງວັນ</DialogTitle>
          <DialogDescription>
            ຍ້າຍວັນຊ້ອມ {formatDateLocal(fromScheduledDate)} ສຳລັບ{" "}
            {performerCount} ຄົນໄປວັນໃໝ່
            {hasStudioBooking && studioSession?.roomName
              ? ` · ມີການຈອງ ${studioSession.roomName}`
              : ""}
          </DialogDescription>
        </DialogHeader>

        <FormRoot<RescheduleDayFormValues>
          methods={methods}
          onSubmit={async (vals) => {
            if (vals.scheduledDate === fromScheduledDate && !hasStudioBooking) {
              return;
            }

            if (hasStudioBooking && vals.bookStudio) {
              const conflict = await studioConflictMonitor.runConflictCheck(
                toRescheduleConflictValues(vals),
              );
              if (!conflict.ok) {
                methods.setError("studioRoomId", {
                  message: conflict.message,
                });
                return;
              }
            }

            await onSubmit({
              fromScheduledDate,
              scheduledDate: vals.scheduledDate,
              studioBooking:
                hasStudioBooking && canBookStudio
                  ? buildStudioBookingPayloadFromSlice(vals)
                  : undefined,
            });
          }}
        >
          <RehearsalStudioConflictProvider value={studioConflictMonitor}>
            <div className="space-y-4 py-2">
              <div>
                <p className="mb-1 font-medium text-sm">ວັນເດີມ</p>
                <p className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
                  {formatDateLocal(fromScheduledDate)}
                </p>
              </div>

              <div>
                <label
                  htmlFor="rehearsal-day-reschedule-date"
                  className="mb-1 block font-medium text-sm"
                >
                  ວັນໃໝ່ <span className="text-destructive">*</span>
                </label>
                <input
                  id="rehearsal-day-reschedule-date"
                  type="date"
                  value={scheduledDate}
                  disabled={submitting}
                  onChange={(e) =>
                    methods.setValue("scheduledDate", e.target.value, {
                      shouldValidate: true,
                    })
                  }
                  className={`flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm ${
                    studioConflictMonitor.conflictDetails.some(
                      (d) => d.date === scheduledDate,
                    )
                      ? "border-destructive"
                      : "border-input"
                  }`}
                />
                {methods.formState.errors.scheduledDate?.message ? (
                  <p className="mt-1 text-destructive text-sm">
                    {methods.formState.errors.scheduledDate.message}
                  </p>
                ) : null}
              </div>

              {hasStudioBooking ? (
                <RehearsalStudioBookingFields
                  variant="reschedule"
                  canBookStudio={canBookStudio}
                  defaultRoomId={studioSession?.roomId}
                  defaultRoomName={studioSession?.roomName}
                  disabled={submitting}
                />
              ) : null}
            </div>

            <RescheduleDialogFooter
              submitting={submitting}
              unchanged={unchanged}
              onCancel={() => onOpenChange(false)}
            />
          </RehearsalStudioConflictProvider>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}
